// [PHASE-5 2026-10] What a school_admin sees while the school's subscription is
// expired or suspended: the reason, the platform contact (from platform
// settings, never hardcoded) and a "Check again" button that re-reads
// /auth/me, so a renewal is picked up without signing out. No data is shown and
// none is deleted; every other route answers 403 until the renewal.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Lock, Phone, Mail, MessageCircle, RefreshCw, LogOut, ShieldCheck } from 'lucide-react';
import { authApi, sessionApi } from '../services/apiService';
import { localizedServerMessage } from '../lib/sessionEvents';
import { renewalEndedOn, telHref, mailtoHref, whatsappHref } from '../lib/renewal';
import SupportButton from './SupportButton';

function formatDate(value, language) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const locale = language === 'ar' ? 'ar' : language === 'fr' ? 'fr-FR' : 'en-GB';
  try {
    return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

const RenewalScreen = ({ lock, onRenewed, onLogout }) => {
  const { t, i18n } = useTranslation();
  const [info, setInfo] = useState(null);
  const [checking, setChecking] = useState(false);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    let cancelled = false;
    sessionApi.getRenewalInfo()
      .then((data) => { if (!cancelled) setInfo(data); })
      .catch(() => { /* the lock detail already carries the contact */ });
    return () => { cancelled = true; };
  }, []);

  const contact = (info && info.contact) || (lock && lock.contact) || {};
  const status = (info && info.status) || (lock && lock.status) || 'expired';
  // Only a date that has passed (a suspended school's period can run on).
  const endedOn = renewalEndedOn(info);
  const message = localizedServerMessage(lock, i18n.language);
  const phoneHref = telHref(contact.phone);
  const emailHref = mailtoHref(contact.email);
  const waHref = whatsappHref(contact.whatsapp);
  const hasContact = !!(phoneHref || emailHref || waHref);

  const checkAgain = async () => {
    setChecking(true);
    try {
      const me = await authApi.getMe();
      if (me && !me.subscription_lock) {
        toast.success(t('renewal_restored'));
        onRenewed(me);
      } else {
        toast.info(t('renewal_still_locked'));
      }
    } catch {
      toast.error(t('renewal_check_failed'));
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-teal-50 dark:from-gray-950 dark:to-gray-900"
    >
      <main className="w-full max-w-xl bg-white dark:bg-gray-900 rounded-3xl shadow-xl border border-slate-200 dark:border-gray-800 overflow-hidden">
        <div className="px-8 pt-8 pb-6 text-center border-b border-slate-100 dark:border-gray-800">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-amber-700 dark:text-amber-300" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-gray-100">
            {status === 'suspended' ? t('renewal_title_suspended') : t('renewal_title')}
          </h1>
          {endedOn && formatDate(endedOn, i18n.language) && (
            <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
              {t('renewal_ended_on', { date: formatDate(endedOn, i18n.language) })}
            </p>
          )}
          {message && (
            <p className="mt-4 text-sm leading-relaxed text-slate-700 dark:text-gray-300">{message}</p>
          )}
          <p className="mt-3 inline-flex items-center gap-2 text-xs text-teal-800 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/30 rounded-full px-3 py-1">
            <ShieldCheck className="w-4 h-4" aria-hidden="true" />
            {t('renewal_data_kept')}
          </p>
        </div>

        <section className="px-8 py-6">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-gray-100 mb-3">
            {t('renewal_contact_title')}
          </h2>
          {hasContact ? (
            <ul className="space-y-2">
              {phoneHref && (
                <li>
                  <a href={phoneHref}
                     className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-gray-700 px-4 py-3 hover:bg-slate-50 dark:hover:bg-gray-800">
                    <Phone className="w-5 h-5 text-teal-700 dark:text-teal-300 shrink-0" aria-hidden="true" />
                    <span className="text-xs text-slate-500 dark:text-gray-400 w-20">{t('renewal_phone')}</span>
                    <span dir="ltr" className="font-mono text-sm text-slate-900 dark:text-gray-100">{contact.phone}</span>
                  </a>
                </li>
              )}
              {emailHref && (
                <li>
                  <a href={emailHref}
                     className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-gray-700 px-4 py-3 hover:bg-slate-50 dark:hover:bg-gray-800">
                    <Mail className="w-5 h-5 text-teal-700 dark:text-teal-300 shrink-0" aria-hidden="true" />
                    <span className="text-xs text-slate-500 dark:text-gray-400 w-20">{t('renewal_email')}</span>
                    <span dir="ltr" className="text-sm text-slate-900 dark:text-gray-100 break-all">{contact.email}</span>
                  </a>
                </li>
              )}
              {waHref && (
                <li>
                  <a href={waHref} target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-gray-700 px-4 py-3 hover:bg-slate-50 dark:hover:bg-gray-800">
                    <MessageCircle className="w-5 h-5 text-teal-700 dark:text-teal-300 shrink-0" aria-hidden="true" />
                    <span className="text-xs text-slate-500 dark:text-gray-400 w-20">{t('renewal_whatsapp')}</span>
                    <span dir="ltr" className="font-mono text-sm text-slate-900 dark:text-gray-100">{contact.whatsapp}</span>
                  </a>
                </li>
              )}
            </ul>
          ) : (
            <p className="text-sm text-slate-500 dark:text-gray-400">{t('renewal_no_contact')}</p>
          )}
        </section>

        <div className="px-8 pb-8 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={checkAgain}
            disabled={checking}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white font-semibold text-sm px-4 py-3"
          >
            <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} aria-hidden="true" />
            {checking ? t('renewal_checking') : t('renewal_check_again')}
          </button>
          {/* [SUPPORT 2026-11] A locked school's admin can still write to support. */}
          <SupportButton page="renewal" locked className="h-auto rounded-xl px-4 py-3" />
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-gray-700 text-slate-700 dark:text-gray-200 font-semibold text-sm px-4 py-3 hover:bg-slate-50 dark:hover:bg-gray-800"
          >
            <LogOut className="w-4 h-4" aria-hidden="true" />
            {t('renewal_logout')}
          </button>
        </div>
      </main>
    </div>
  );
};

export default RenewalScreen;
